// ── Assignment API hooks ───────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { Assignment, CreateAssignmentBody, TransitionStateBody, ReassignBody } from '@grc/shared';

// ── Query keys ────────────────────────────────────────────────────────────────

function assignmentsKey(tenantId: string | null, controlId: string) {
  return ['assignments', tenantId, controlId] as const;
}

function assignmentKey(tenantId: string | null, id: string) {
  return ['assignment', tenantId, id] as const;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useAssignments(controlId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: assignmentsKey(tenantId, controlId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/assignments?control_id=${controlId}`);
      return data.data as Assignment[];
    },
    enabled: !!tenantId && !!controlId,
    staleTime: 0,
  });
}

export function useAssignment(id: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: assignmentKey(tenantId, id),
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/assignments/${id}`);
      return data.data as Assignment & { history: unknown[] };
    },
    enabled: !!tenantId && !!id,
    staleTime: 0,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useCreateAssignment(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateAssignmentBody) => {
      const { data } = await apiClient.post('/compliance/assignments', body);
      return data.data as Assignment;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: assignmentsKey(tenantId, controlId) });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useTransitionAssignment(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: TransitionStateBody & { id: string }) => {
      const { data } = await apiClient.patch(`/compliance/assignments/${id}/state`, body);
      return data.data as Assignment;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: assignmentsKey(tenantId, controlId) });
      qc.invalidateQueries({ queryKey: assignmentKey(tenantId, updated.id) });
      // Refresh ControlTrackerPage table status badges (l3Map via useFrameworkAssignments).
      // Prefix invalidation covers all frameworkIds without needing to thread frameworkId here.
      qc.invalidateQueries({ queryKey: ['framework-assignments', tenantId] });
      // Refresh MyAssignmentsPage (all filter variants via prefix).
      qc.invalidateQueries({ queryKey: ['my-assignments', tenantId] });
      // Refresh workload and readiness — status transitions shift counts in both.
      qc.invalidateQueries({ queryKey: ['compliance-workload', tenantId] });
      qc.invalidateQueries({ queryKey: ['compliance-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['framework-readiness', tenantId] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}

export function useReassignAssignment(controlId: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: ReassignBody & { id: string }) => {
      const { data } = await apiClient.patch(`/compliance/assignments/${id}/reassign`, body);
      return data.data as Assignment;
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: assignmentsKey(tenantId, controlId) });
      qc.invalidateQueries({ queryKey: assignmentKey(tenantId, updated.id) });
    },
  });
}

// Fetches all active L3 assignments for every control in a framework (single request).
// Used by ControlTrackerPage to compute inheritance badge without per-row calls.
export function useFrameworkAssignments(frameworkId: string) {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['framework-assignments', tenantId, frameworkId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/compliance/assignments?framework_id=${frameworkId}`);
      return data.data as Assignment[];
    },
    enabled: !!tenantId && !!frameworkId,
    staleTime: 0,
  });
}

export function useDeleteAssignment(controlId: string, frameworkId?: string) {
  const { tenantId } = useAuthStore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/compliance/assignments/${id}`);
    },
    onSuccess: (_data, deletedId) => {
      // Refresh the per-control list so the panel re-evaluates hasL3
      qc.invalidateQueries({ queryKey: assignmentsKey(tenantId, controlId) });
      // Remove the detail cache for the deleted assignment so stale data can't resurface
      qc.removeQueries({ queryKey: assignmentKey(tenantId, deletedId) });
      // Refresh framework-level assignments so the table badge (l3Map) updates
      if (frameworkId) {
        qc.invalidateQueries({ queryKey: ['framework-assignments', tenantId, frameworkId] });
      }
      qc.invalidateQueries({ queryKey: ['dashboard', 'compliance', tenantId] });
    },
  });
}
